import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const INACTIVITY_LOCK_MINUTES = 15;
const INACTIVITY_LOGOUT_MINUTES = Number(process.env.REACT_APP_INACTIVITY_LOGOUT_MINUTES || 60);
const STORAGE_KEY = 'app_session_lock_state_v1';
const SESSION_ID_KEY = 'app_session_id_v1';

const SessionLockContext = createContext(null);

const createSessionId = () => {
  const random = Math.random().toString(36).slice(2);
  const timestamp = Date.now().toString(36);
  return `${timestamp}-${random}`;
};

const readStoredState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const writeStoredState = (state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
  }
};

const readSessionId = () => {
  try {
    const existing = localStorage.getItem(SESSION_ID_KEY);
    if (existing && typeof existing === 'string' && existing.trim().length > 0) {
      return existing;
    }
  } catch {
  }
  const next = createSessionId();
  try {
    localStorage.setItem(SESSION_ID_KEY, next);
  } catch {
  }
  return next;
};

export const SessionLockProvider = ({ children }) => {
  const { user, logout } = useAuth();
  const [sessionId] = useState(() => readSessionId());
  const [isLocked, setIsLocked] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [lockedAt, setLockedAt] = useState(null);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [unlockError, setUnlockError] = useState('');
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [status, setStatus] = useState('UNLOCKED');
  const [lastActivityAt, setLastActivityAt] = useState(() => Date.now());
  const lastActivityAtRef = useRef(Date.now());

  const lockTimeoutRef = useRef(null);
  const logoutTimeoutRef = useRef(null);
  const channelRef = useRef(null);

  useEffect(() => {
    if (typeof BroadcastChannel !== 'undefined') {
      channelRef.current = new BroadcastChannel('session-lock');
      channelRef.current.onmessage = (event) => {
        const data = event.data;
        if (!data || data.type !== 'SESSION_LOCK_STATE') {
          return;
        }
        const payload = data.payload || {};
        setIsLocked(Boolean(payload.isLocked));
        setIsLoggingOut(Boolean(payload.isLoggingOut));
        setLockedAt(payload.lockedAt ? new Date(payload.lockedAt) : null);
        setFailedAttempts(Number(payload.failedAttempts || 0));
        setStatus(payload.status || 'UNLOCKED');
      };
    }
    return () => {
      if (channelRef.current) {
        channelRef.current.close();
        channelRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const stored = readStoredState();
    if (stored && stored.isLocked && stored.lockedAt) {
      setIsLocked(true);
      setLockedAt(new Date(stored.lockedAt));
      setFailedAttempts(Number(stored.failedAttempts || 0));
      setStatus(stored.status || 'LOCKED');
    }
  }, []);

  const broadcastState = useCallback(
    (next) => {
      const payload = {
        isLocked: next.isLocked,
        isLoggingOut: next.isLoggingOut,
        lockedAt: next.lockedAt,
        failedAttempts: next.failedAttempts,
        status: next.status
      };
      writeStoredState(payload);
      if (channelRef.current) {
        channelRef.current.postMessage({
          type: 'SESSION_LOCK_STATE',
          payload
        });
      }
    },
    []
  );

  useEffect(() => {
    if (!user) {
      setIsLocked(false);
      setIsLoggingOut(false);
      setLockedAt(null);
      setFailedAttempts(0);
      setUnlockError('');
      setUnlockLoading(false);
      setStatus('UNLOCKED');
      setLastActivityAt(Date.now());
      writeStoredState({
        isLocked: false,
        isLoggingOut: false,
        lockedAt: null,
        failedAttempts: 0,
        status: 'UNLOCKED'
      });
      return;
    }

    let cancelled = false;

    const fetchStatus = async () => {
      try {
        const res = await api.get('/session-lock/status', {
          params: { session_id: sessionId }
        });
        if (cancelled) {
          return;
        }
        const data = res.data || {};
        const locked = Boolean(data.locked);
        setIsLocked(locked);
        setLockedAt(data.locked_at ? new Date(data.locked_at) : null);
        setFailedAttempts(Number(data.unlock_failed_attempts || 0));
        setStatus(data.status || (locked ? 'LOCKED' : 'UNLOCKED'));
        broadcastState({
          isLocked: locked,
          isLoggingOut: false,
          lockedAt: data.locked_at || null,
          failedAttempts: Number(data.unlock_failed_attempts || 0),
          status: data.status || (locked ? 'LOCKED' : 'UNLOCKED')
        });
      } catch (error) {
        setIsLocked(false);
        setLockedAt(null);
        setFailedAttempts(0);
        setStatus('UNLOCKED');
        broadcastState({
          isLocked: false,
          isLoggingOut: false,
          lockedAt: null,
          failedAttempts: 0,
          status: 'UNLOCKED'
        });
      }
    };

    fetchStatus();

    return () => {
      cancelled = true;
    };
  }, [user, sessionId, broadcastState]);

  const clearTimers = useCallback(() => {
    if (lockTimeoutRef.current) {
      clearTimeout(lockTimeoutRef.current);
      lockTimeoutRef.current = null;
    }
    if (logoutTimeoutRef.current) {
      clearTimeout(logoutTimeoutRef.current);
      logoutTimeoutRef.current = null;
    }
  }, []);

  const lockSession = useCallback(async () => {
    if (!user || isLocked || isLoggingOut) {
      return;
    }
    const now = new Date();
    setIsLocked(true);
    setLockedAt(now);
    setStatus('LOCKED');
    setUnlockError('');
    broadcastState({
      isLocked: true,
      isLoggingOut: false,
      lockedAt: now.toISOString(),
      failedAttempts,
      status: 'LOCKED'
    });

    try {
      await api.post('/session-lock/lock', {
        session_id: sessionId
      });
    } catch {
    }
  }, [user, isLocked, isLoggingOut, broadcastState, sessionId, failedAttempts]);

  const autoLogout = useCallback(
    async () => {
      if (!user || isLoggingOut) {
        return;
      }
      setIsLoggingOut(true);
      broadcastState({
        isLocked,
        isLoggingOut: true,
        lockedAt: lockedAt ? lockedAt.toISOString() : null,
        failedAttempts,
        status: 'LOGGED_OUT'
      });
      try {
        await api.post('/session-lock/auto-logout', {
          session_id: sessionId
        });
      } catch {
      } finally {
        logout();
        writeStoredState({
          isLocked: false,
          isLoggingOut: false,
          lockedAt: null,
          failedAttempts: 0,
          status: 'UNLOCKED'
        });
        window.location.href = '/login';
      }
    },
    [user, isLoggingOut, broadcastState, isLocked, lockedAt, failedAttempts, sessionId, logout]
  );

  const scheduleTimers = useCallback(
    (referenceTime) => {
      clearTimers();
      if (!user || isLoggingOut) {
        return;
      }

      const now = Date.now();
      const last = referenceTime || lastActivityAt || now;
      const lockMs = INACTIVITY_LOCK_MINUTES * 60 * 1000;
      const logoutMs = INACTIVITY_LOGOUT_MINUTES * 60 * 1000;

      const msSinceLast = Math.max(0, now - last);
      const lockDelay = Math.max(0, lockMs - msSinceLast);
      const logoutDelay = Math.max(0, logoutMs - msSinceLast);

      lockTimeoutRef.current = setTimeout(() => {
        lockSession();
      }, lockDelay);

      logoutTimeoutRef.current = setTimeout(() => {
        autoLogout();
      }, logoutDelay);
    },
    [user, isLocked, isLoggingOut, lastActivityAt, clearTimers, lockSession, autoLogout]
  );

  useEffect(() => {
    if (!user) {
      clearTimers();
      return;
    }
    scheduleTimers();
  }, [user, isLocked, isLoggingOut, lastActivityAt, scheduleTimers, clearTimers]);

  const registerActivity = useCallback(() => {
    if (!user) {
      return;
    }
    const now = Date.now();
    lastActivityAtRef.current = now;
    setLastActivityAt(now);
    if (!isLocked && !isLoggingOut) {
      scheduleTimers(now);
    }
  }, [user, isLocked, isLoggingOut, scheduleTimers]);

  useEffect(() => {
    const handleActivity = () => {
      registerActivity();
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'focus'];

    events.forEach((eventName) => {
      window.addEventListener(eventName, handleActivity, true);
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (!user || isLocked || isLoggingOut) {
          return;
        }
        const now = Date.now();
        const last = lastActivityAtRef.current || now;
        const msSinceLast = Math.max(0, now - last);
        const lockMs = INACTIVITY_LOCK_MINUTES * 60 * 1000;
        const logoutMs = INACTIVITY_LOGOUT_MINUTES * 60 * 1000;
        if (msSinceLast >= logoutMs) {
          autoLogout();
          return;
        }
        if (msSinceLast >= lockMs) {
          lockSession();
          return;
        }
        registerActivity();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      events.forEach((eventName) => {
        window.removeEventListener(eventName, handleActivity, true);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [registerActivity, user, isLocked, isLoggingOut, lockSession, autoLogout]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key !== STORAGE_KEY || !event.newValue) {
        return;
      }
      try {
        const data = JSON.parse(event.newValue);
        setIsLocked(Boolean(data.isLocked));
        setIsLoggingOut(Boolean(data.isLoggingOut));
        setLockedAt(data.lockedAt ? new Date(data.lockedAt) : null);
        setFailedAttempts(Number(data.failedAttempts || 0));
        setStatus(data.status || 'UNLOCKED');
      } catch {
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const unlockSession = useCallback(
    async (password) => {
      if (!user || !isLocked || isLoggingOut || unlockLoading) {
        return;
      }
      setUnlockLoading(true);
      setUnlockError('');
      try {
        const res = await api.post('/session-lock/unlock', {
          session_id: sessionId,
          password
        });
        const data = res.data || {};
        const attempts = Number(data.unlock_failed_attempts || 0);
        setFailedAttempts(attempts);
        setIsLocked(false);
        setStatus('UNLOCKED');
        const now = Date.now();
        lastActivityAtRef.current = now;
        setLastActivityAt(now);
        broadcastState({
          isLocked: false,
          isLoggingOut: false,
          lockedAt: null,
          failedAttempts: attempts,
          status: 'UNLOCKED'
        });
        scheduleTimers(now);
      } catch (error) {
        const response = error.response;
        if (response && response.status === 400) {
          const message = response.data && response.data.message ? response.data.message : 'Password errata';
          const attempts = Number(
            response.data && response.data.unlock_failed_attempts != null
              ? response.data.unlock_failed_attempts
              : failedAttempts + 1
          );
          setUnlockError(message);
          setFailedAttempts(attempts);
          broadcastState({
            isLocked: true,
            isLoggingOut: false,
            lockedAt: lockedAt ? lockedAt.toISOString() : null,
            failedAttempts: attempts,
            status: 'LOCKED'
          });
        } else if (response && (response.status === 401 || response.status === 403)) {
          setIsLocked(false);
          setStatus('UNLOCKED');
          setIsLoggingOut(true);
          broadcastState({
            isLocked: false,
            isLoggingOut: true,
            lockedAt: null,
            failedAttempts,
            status: 'LOGGED_OUT'
          });
          logout();
          window.location.href = '/login';
        } else {
          setUnlockError('Errore durante lo sblocco della sessione');
        }
      } finally {
        setUnlockLoading(false);
      }
    },
    [
      user,
      isLocked,
      isLoggingOut,
      unlockLoading,
      sessionId,
      broadcastState,
      scheduleTimers,
      lockedAt,
      failedAttempts,
      logout
    ]
  );

  const manualLogoutFromLock = useCallback(async () => {
    await autoLogout();
  }, [autoLogout]);

  const clearUnlockError = useCallback(() => {
    setUnlockError('');
  }, []);

  const value = useMemo(
    () => ({
      sessionId,
      isLocked,
      isLoggingOut,
      lockedAt,
      failedAttempts,
      status,
      unlockError,
      unlockLoading,
      registerActivity,
      lockSession,
      unlockSession,
      manualLogoutFromLock,
      clearUnlockError
    }),
    [
      sessionId,
      isLocked,
      isLoggingOut,
      lockedAt,
      failedAttempts,
      status,
      unlockError,
      unlockLoading,
      registerActivity,
      lockSession,
      unlockSession,
      manualLogoutFromLock,
      clearUnlockError
    ]
  );

  return <SessionLockContext.Provider value={value}>{children}</SessionLockContext.Provider>;
};

export const useSessionLock = () => {
  const ctx = useContext(SessionLockContext);
  if (!ctx) {
    throw new Error('useSessionLock must be used within a SessionLockProvider');
  }
  return ctx;
};
